import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ClassroomGridCard } from '@Components/AbwaabPlus'
import { NumberFormatter } from '@Utilities/Formatters'

type Props = {
	bundle: Bundle
	/** /mine rows and /search/bundles (no ownership merge) — force it here */
	owned?: boolean
	className?: string
}

/**
 * The one bundle browse card — same shell as the classroom/course cards. No
 * education rows (bundles carry none); the info slot shows the member-course
 * count instead. Ownership is binary, so there is never an expired badge —
 * expiry lives on the member-course enrollments.
 */
const BundleCard: React.FC<Props> = ({ bundle, owned: ownedProp, className }) => {
	const [t] = useTranslation('bundle')
	const { t: tGen } = useTranslation('generic')
	const navigate = useNavigate()

	const owned = ownedProp ?? (!!bundle.owned || !!bundle.enrollment)
	const instructorUser = bundle.instructor?.user
	const instructorName = instructorUser ? `${instructorUser.first_name ?? ''} ${instructorUser.last_name ?? ''}`.trim() : undefined
	// Included-course names with the same first + "+x" collapse the classroom
	// card uses for education years; count-only fallback for older payloads.
	const includedTitles = bundle.course_titles ?? []
	const typeLabels = includedTitles.length
		? [`${t('includes', { defaultValue: 'يشمل' })}: ${includedTitles[0]}`, ...includedTitles.slice(1)]
		: bundle.courses_count != null
		? [t('courses_count', { count: bundle.courses_count, defaultValue: '{{count}} كورسات' })]
		: []

	return (
		<ClassroomGridCard
			className={className}
			title={bundle.title}
			instructorName={instructorName}
			typeLabels={typeLabels}
			mode={t('bundle_mode', { defaultValue: 'باقة' })}
			photo={bundle.thumbnail}
			emoji="🎒"
			tint="var(--yellow-12, #fef9c3)"
			enrolled={owned}
			enrolledLabel={t('owned_chip', { defaultValue: 'مشترك' })}
			priceText={
				owned ? undefined : Number(bundle.price) === 0 ? tGen('free', { defaultValue: 'مجاني' }) : `${NumberFormatter(bundle.price)} ${tGen('egp')}`
			}
			ctaLabel={t('subscribe_cta', { defaultValue: 'اشترك' })}
			onClick={() => navigate(`/bundles/${bundle.label}`)}
		/>
	)
}

export default BundleCard
